import { cn } from '../cn';

export interface SegmentOption<V extends string | number> {
  value: V;
  label: string;
  disabled?: boolean;
}
export interface SegmentedControlProps<V extends string | number> {
  value: V;
  options: readonly SegmentOption<V>[];
  onChange: (v: V) => void;
  /** Full-width grid (mobile forms) vs. inline pill group. */
  fullWidth?: boolean;
  size?: 'desktop' | 'mobile' | 'responsive';
  wrap?: boolean;
  className?: string;
  'aria-label'?: string;
}

/**
 * Design `seg()`: 2 px padding, 2 px gap, bg2 + line2 border, radius 8 (9 when full width);
 * items 28 px desktop / 36 px mobile, radius 6, 500 12px; on = fg fill with bg text.
 */
export function SegmentedControl<V extends string | number>({ value, options, onChange, fullWidth, size = 'responsive', wrap, className, ...a11y }: SegmentedControlProps<V>) {
  return (
    <div
      role="radiogroup"
      aria-label={a11y['aria-label']}
      className={cn(
        'gap-[2px] border border-line2 bg-bg2 p-[2px]',
        fullWidth ? 'grid grid-flow-col auto-cols-fr rounded-[9px]' : 'inline-flex self-start rounded-lg',
        wrap && 'flex-wrap',
        className,
      )}
    >
      {options.map((o) => {
        const on = o.value === value;
        return (
          <button
            key={String(o.value)}
            type="button"
            role="radio"
            aria-checked={on}
            disabled={o.disabled}
            onClick={() => onChange(o.value)}
            className={cn(
              'whitespace-nowrap rounded border-0 px-[11px] text-base font-medium leading-none disabled:opacity-40',
              size === 'desktop' && 'h-7',
              size === 'mobile' && 'h-9',
              size === 'responsive' && 'h-9 md:h-7',
              on ? 'bg-fg text-bg' : 'bg-transparent text-fg3 hover:text-fg',
            )}
          >
            {o.label}
          </button>
        );
      })}
    </div>
  );
}
